import type { DomainRouterJsonInput } from "@triedge/domain-core";
import type { IntegrationStatus, ModuleConfig } from "./index";

export type ModuleConfigResponse = ModuleConfig & {
  integrationStatus?: IntegrationStatus | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
};

export type FinancialAidHistoricalImportTemplateResponse = {
  downloadUrl: string;
  templateVersion: string;
  fileName: string;
  expiresAt: number;
};

export type FinancialAidHistoricalImportUploadResponse = {
  importId: string;
  uploadUrl: string;
  s3Key: string;
  expiresAt: number;
};

export type FinancialAidHistoricalImportStatusResponse = {
  status: string;
  importId?: string | null;
  templateVersion?: string | null;
  lastFileName?: string | null;
  lastS3Key?: string | null;
  lastImportedAt?: string | null;
  processedRows?: number | null;
  acceptedRows?: number | null;
  rejectedRows?: number | null;
  errorSummary?: string | null;
};

export type StaffDirectoryUserRole =
  | "super-administrator"
  | "admin"
  | "principal"
  | "teacher"
  | "nurse"
  | "accounts";

export type StaffDirectoryUserStatus = "active" | "invited" | "deactivated";

export type StaffDirectoryUser = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: StaffDirectoryUserRole;
  status: StaffDirectoryUserStatus;
  lastActiveAt: string | null;
  invitedAt: string | null;
  deactivatedAt: string | null;
  activationUrl: string | null;
};

export type StaffUserListResponse = {
  items: StaffDirectoryUser[];
  totalCount: number;
  page: number;
  totalPages: number;
};

/**
 * Result from creating or retrying an invited staff-directory user.
 *
 * `userId` may identify a newly created row or an existing invited row when
 * the same email is submitted again. Duplicate invited rows must not be
 * created.
 *
 * `emailQueued=false` means the staff invite exists but the staff invitation
 * email intent was not accepted by the communications outbox. Callers must
 * surface that delivery state instead of treating the invite as fully
 * delivered.
 */
export type InviteStaffUserResponse = {
  userId: string;
  activationUrl: string;
  emailQueued: boolean;
  invitedAt: string;
};

/**
 * Result from queueing a reminder for an existing invited staff-directory row.
 *
 * Resend operations must not create a replacement staff record.
 */
export type ResendStaffInviteResponse = {
  userId: string;
  activationUrl: string;
  emailQueued: boolean;
  invitedAt: string;
  resentAt: string;
};

export type UpdateStaffUserRoleResponse = {
  userId: string;
  updatedAt: string;
  role: StaffDirectoryUserRole;
};

export type DeactivateStaffUserResponse = {
  userId: string;
  deactivatedAt: string;
};

export type ReactivateStaffUserResponse = {
  userId: string;
  reactivatedAt: string;
};

export type DeleteStaffUserResponse = {
  userId: string;
  deletedAt: string;
  reassignedToUserId: string | null;
  deleteWithoutAssigning: boolean;
  reassignment: {
    required: boolean;
    admissionsCount: number;
    enrollmentCount: number;
    financialAidCount: number;
    taskCount: number;
  };
};

export type PendingStaffRequestStatus = "pending" | "approved" | "rejected";

export type PendingStaffRequestRecord = {
  requestId: string;
  email: string;
  username: string;
  userSub: string | null;
  userPoolId: string | null;
  schoolId: string | null;
  firstName: string | null;
  lastName: string | null;
  requestedRole: StaffDirectoryUserRole;
  status: PendingStaffRequestStatus;
  source: string | null;
  requestedAt: string;
  lastSeenAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  staffUserId: string | null;
};

export type PendingStaffRequestListResponse = {
  items: PendingStaffRequestRecord[];
  totalCount: number;
};

export type ApprovePendingStaffRequestResponse = {
  requestId: string;
  userId: string;
  approvedAt: string;
  role: Exclude<StaffDirectoryUserRole, "super-administrator">;
};

export type RejectPendingStaffRequestResponse = {
  requestId: string;
  rejectedAt: string;
};

export type ApprovedDomainRecord = {
  domain: string;
  addedAt: string;
  addedBy: string;
};

export type ApprovedDomainListResponse = {
  items: ApprovedDomainRecord[];
};

export type StaffSignupCodeStatus = "active" | "revoked";

export type StaffSignupCodeRecord = {
  codeId: string;
  label: string;
  maskedValue: string;
  status: StaffSignupCodeStatus;
  createdAt: string;
};

export type StaffSignupCodeListResponse = {
  items: StaffSignupCodeRecord[];
};

export type CreateStaffCodeResponse = StaffSignupCodeRecord & {
  fullValue: string;
};

export type RotateStaffCodeResponse = StaffSignupCodeRecord & {
  fullValue: string;
  rotatedAt?: string | null;
};

export type RevokeStaffCodeResponse = {
  codeId: string;
  revokedAt: string;
};

export type ModuleEnablementAuditAction =
  | "enabled"
  | "disabled"
  | "route_group_enabled"
  | "route_group_disabled";

export type ModuleEnablementAuditEvent = {
  eventId: string;
  actorEmail: string;
  actorRole: "admin" | "owner" | "super-administrator";
  moduleId: string;
  moduleLabel: string;
  action: ModuleEnablementAuditAction;
  occurredAt: string;
  routeGroupId?: string | null;
  routeGroupLabel?: string | null;
};

export type ListModuleEnablementAuditEventsResponse = {
  events: ModuleEnablementAuditEvent[];
};

export const STAFF_SETTINGS_QUERY_OPERATION_IDS = [
  "getModuleConfig",
  "listStaffUsers",
  "listStaffCodes",
  "listPendingStaffRequests",
  "listApprovedDomains",
  "listModuleEnablementAuditEvents",
  "getFinancialAidHistoricalImportTemplate",
  "getFinancialAidHistoricalImportStatus",
] as const;

export const STAFF_SETTINGS_MUTATION_OPERATION_IDS = [
  "updateModuleConfig",
  "inviteStaffUser",
  "resendStaffInvite",
  "updateStaffUserRole",
  "deactivateStaffUser",
  "reactivateStaffUser",
  "deleteStaffUser",
  "approvePendingStaffRequest",
  "rejectPendingStaffRequest",
  "createStaffCode",
  "rotateStaffCode",
  "revokeStaffCode",
  "addApprovedDomain",
  "removeApprovedDomain",
  "requestFinancialAidHistoricalImportUpload",
  "startFinancialAidHistoricalImport",
] as const;

export type StaffSettingsQueryOperationId =
  (typeof STAFF_SETTINGS_QUERY_OPERATION_IDS)[number];
export type StaffSettingsMutationOperationId =
  (typeof STAFF_SETTINGS_MUTATION_OPERATION_IDS)[number];

export type StaffSettingsQueryArgsMap = {
  getModuleConfig: { orgId?: string | null; moduleKey: string };
  listStaffUsers: {
    search?: string;
    statusFilter?: "all" | StaffDirectoryUserStatus;
    page?: number;
    limit?: number;
  };
  listStaffCodes: Record<string, never>;
  listPendingStaffRequests: {
    status?: "all" | PendingStaffRequestStatus;
    schoolId?: string | null;
    orgId?: string | null;
    moduleKey?: string | null;
  };
  listApprovedDomains: Record<string, never>;
  listModuleEnablementAuditEvents: {
    schoolId?: string | null;
    limit?: number;
  };
  getFinancialAidHistoricalImportTemplate: {
    orgId?: string | null;
    moduleKey?: string | null;
  };
  getFinancialAidHistoricalImportStatus: {
    orgId?: string | null;
    moduleKey?: string | null;
    importId?: string | null;
  };
};

export type StaffSettingsQueryResultMap = {
  getModuleConfig: ModuleConfigResponse;
  listStaffUsers: StaffUserListResponse;
  listStaffCodes: StaffSignupCodeListResponse;
  listPendingStaffRequests: PendingStaffRequestListResponse;
  listApprovedDomains: ApprovedDomainListResponse;
  listModuleEnablementAuditEvents: ListModuleEnablementAuditEventsResponse;
  getFinancialAidHistoricalImportTemplate: FinancialAidHistoricalImportTemplateResponse;
  getFinancialAidHistoricalImportStatus: FinancialAidHistoricalImportStatusResponse;
};

export type StaffSettingsMutationArgsMap = {
  updateModuleConfig: { input: DomainRouterJsonInput };
  inviteStaffUser: { input: DomainRouterJsonInput };
  resendStaffInvite: { input: DomainRouterJsonInput };
  updateStaffUserRole: { input: DomainRouterJsonInput };
  deactivateStaffUser: { input: DomainRouterJsonInput };
  reactivateStaffUser: { input: DomainRouterJsonInput };
  deleteStaffUser: { input: DomainRouterJsonInput };
  approvePendingStaffRequest: { input: DomainRouterJsonInput };
  rejectPendingStaffRequest: { input: DomainRouterJsonInput };
  createStaffCode: { input: DomainRouterJsonInput };
  rotateStaffCode: { input: DomainRouterJsonInput };
  revokeStaffCode: { input: DomainRouterJsonInput };
  addApprovedDomain: { input: DomainRouterJsonInput };
  removeApprovedDomain: { input: DomainRouterJsonInput };
  requestFinancialAidHistoricalImportUpload: { input: DomainRouterJsonInput };
  startFinancialAidHistoricalImport: { input: DomainRouterJsonInput };
};

export type StaffSettingsMutationResultMap = {
  updateModuleConfig: ModuleConfigResponse;
  inviteStaffUser: InviteStaffUserResponse;
  resendStaffInvite: ResendStaffInviteResponse;
  updateStaffUserRole: UpdateStaffUserRoleResponse;
  deactivateStaffUser: DeactivateStaffUserResponse;
  reactivateStaffUser: ReactivateStaffUserResponse;
  deleteStaffUser: DeleteStaffUserResponse;
  approvePendingStaffRequest: ApprovePendingStaffRequestResponse;
  rejectPendingStaffRequest: RejectPendingStaffRequestResponse;
  createStaffCode: CreateStaffCodeResponse;
  rotateStaffCode: RotateStaffCodeResponse;
  revokeStaffCode: RevokeStaffCodeResponse;
  addApprovedDomain: ApprovedDomainRecord;
  removeApprovedDomain: ApprovedDomainRecord;
  requestFinancialAidHistoricalImportUpload: FinancialAidHistoricalImportUploadResponse;
  startFinancialAidHistoricalImport: FinancialAidHistoricalImportStatusResponse;
};
